import * as React from 'react';
import { navigate } from 'gatsby';
import '../i18n/i18n';
import LoadingGate from '../components/03_organisms/LoadingGate/LoadingGate';
import useAuth from '../hooks/useAuth';
import API from '../services/API';
import { useTranslation } from 'react-i18next';
import { getVoucherErrorMessage, isVoucherRejected } from '../utils/authError';

const pendingVoucherStorageKey = 'stretchme.pendingVoucherCode';

/**
 * Will be called as the following shows:
 * /callback#access_token=<value>&id_token=<value>&refresh_token=<value>
 *
 * The aws-amplify handles the query parameters. We just need to wait til the user is logged in

 * @see {@link https://github.com/aws-amplify/amplify-js/blob/main/packages/auth/src/OAuth/OAuth.ts#L217}
 *
 */
function CallbackPage() {
  const { t } = useTranslation();
  const { isSignedIn, status, token } = useAuth();
  const [callbackError, setCallbackError] = React.useState<unknown>();
  const [callbackTimedOut, setCallbackTimedOut] = React.useState(false);
  const [retryNonce, setRetryNonce] = React.useState(0);
  const processingRef = React.useRef(false);

  React.useEffect(() => {
    if (isSignedIn || status === 'error') return;
    const timeoutId = window.setTimeout(() => setCallbackTimedOut(true), 20000);
    return () => window.clearTimeout(timeoutId);
  }, [isSignedIn, status]);

  React.useEffect(() => {
    console.log('callback()', { isSignedIn });
    if (!isSignedIn || !token || processingRef.current) {
      return;
    }
    processingRef.current = true;
    setCallbackError(undefined);

    const finishSocialLogin = async () => {
      const voucherCode = window.sessionStorage.getItem(
        pendingVoucherStorageKey,
      );
      try {
        if (voucherCode) {
          await API.applyVoucher(token, voucherCode);
          window.sessionStorage.removeItem(pendingVoucherStorageKey);
        }
        await navigate(`/app`);
      } catch (error) {
        if (isVoucherRejected(error)) {
          window.sessionStorage.removeItem(pendingVoucherStorageKey);
        }
        processingRef.current = false;
        setCallbackError(error);
      }
    };

    finishSocialLogin();
  }, [isSignedIn, retryNonce, token]);

  if (callbackError != null) {
    const voucherRejected = isVoucherRejected(callbackError);
    return (
      <main style={{ margin: '4rem auto', maxWidth: 560, padding: '1rem' }}>
        <h1>{t('SORRY')}</h1>
        <p>{getVoucherErrorMessage(t, callbackError)}</p>
        <button
          type="button"
          onClick={() => {
            if (voucherRejected) {
              window.location.assign('/app');
              return;
            }
            processingRef.current = false;
            setRetryNonce((value) => value + 1);
          }}
        >
          {voucherRejected ? t('voucher_activated_label') : t('TRY_AGAIN')}
        </button>
      </main>
    );
  }

  if (status === 'error' || callbackTimedOut) {
    return (
      <main style={{ margin: '4rem auto', maxWidth: 560, padding: '1rem' }}>
        <h1>{t('SORRY')}</h1>
        <p>{t('AUTH_UNEXPECTED_ERROR', { code: 'SocialLoginCallback' })}</p>
        <a href="/login">{t('LOGIN_BUTTON_LINK')}</a>
      </main>
    );
  }

  return <LoadingGate />;
}

export default CallbackPage;
